import Rectangle = createjs.Rectangle;
import Shape = createjs.Shape;
import DisplayObject = createjs.DisplayObject;
import Point = createjs.Point;
import MouseEvent = createjs.MouseEvent;
import { LoaderInfo } from "../net/LoaderInfo";
import { CREATEJS_UTILITIES_LOGGER, TextField } from "../../../../../index";
import { getLogger } from "@goodgamestudios/logger";
import Container = createjs.Container;
import { objectsUnderPoint } from "./objectsUnderPoint";
import Stage = createjs.Stage;

const PRESS_MOVE: string = "pressmove";
const logger = getLogger(CREATEJS_UTILITIES_LOGGER);
const proto: any = (createjs.Container.prototype as any);
type ContainerWithNewShape = { _newshape?: createjs.Shape } & createjs.Container;

Object.defineProperty(createjs.Container.prototype, "shape", {
    get: function (this: ContainerWithNewShape): createjs.Shape {
        if (!this._newshape) {
            this._newshape = new createjs.Shape();
            this.addChild(this._newshape);
        }
        return this._newshape;
    },
    set: function (this: ContainerWithNewShape, value: createjs.Shape): never {
        throw new Error("Can't set shape of container");
    },
    enumerable: true,
    configurable: true,
});

Object.defineProperty(createjs.Container.prototype, "graphics", {
    get: function (this: createjs.Container): createjs.Graphics {
        return this.shape.graphics;
    },
    set: function (this: createjs.Container, value: createjs.Graphics) {
        this.shape.graphics = value;
    },
    enumerable: true,
    configurable: true,
});

createjs.Container.prototype.addChild = function (this: ContainerWithNewShape, child: createjs.DisplayObject): createjs.DisplayObject {
    if (child == null) {
        return child;
    }

    let l = arguments.length;
    if (l > 1) {
        for (let i = 0; i < l; i++) {
            this.addChild(arguments[i]);
        }
        return arguments[l - 1];
    }

    if (child.parent) {
        //we need to remove information in children of parent about this child, we should not call .removeChild because in that case event "removed" will be called.
        let index = createjs.indexOf(child.parent.children, child);
        if (index > -1) {
            child.parent.children.splice(index, 1);
            child.parent = null;
        }
    }

    child.parent = this;
    this.children.push(child);
    child.added();

    return child;
};

createjs.Container.prototype.addChildAt = function (this: ContainerWithNewShape, child: createjs.DisplayObject, index: number): createjs.DisplayObject {
    let l = arguments.length;
    let indx = arguments[l - 1]; // can't use the same name as the index param or it replaces arguments[1]
    if (indx < 0 || indx > this.children.length) {
        return arguments[l - 2];
    }

    if (l > 2) {
        for (let i = 0; i < l - 1; i++) {
            this.addChildAt(arguments[i], indx + i);
        }
        return arguments[l - 2];
    }

    if (child.parent) {
        //we need to remove information in children of parent about this child, we should not call .removeChild because in that case event "removed" will be called.
        let oldIndex = createjs.indexOf(child.parent.children, child);
        if (oldIndex > -1) {
            child.parent.children.splice(oldIndex, 1);
            child.parent = null;
        }
    }

    child.parent = this;
    this.children.splice(index, 0, child);
    child.added();

    return child;
};

createjs.Container.prototype.removeChildAt = function (this: ContainerWithNewShape, index: number): boolean {

    let l = arguments.length;
    if (l > 1) {
        const a: any[] = [];
        for (let i = 0; i < l; i++) {
            a[i] = arguments[i];
        }

        a.sort(function (a: any, b: any) {
            return b - a;
        });

        let good = true;
        for (let i = 0; i < l; i++) {
            good = good && this.removeChildAt(a[i]);
        }
        return good;
    }

    if (index < 0 || index > this.children.length - 1) {
        return false;
    }

    let child = this.children[index];
    if (child) {
        child.removed();
    }
    this.children.splice(index, 1);

    return true;
};

Object.defineProperty(createjs.Container.prototype, "buttonMode", {
    value: false,
    enumerable: true,
    configurable: true,
    writable: true,
});

Object.defineProperty(createjs.Container.prototype, "doubleClickEnabled", {
    value: false,
    enumerable: true,
    configurable: true,
    writable: true,
});

Object.defineProperty(createjs.Container.prototype, "tabChildren", {
    value: true,
    enumerable: true,
    configurable: true,
    writable: true,
});

Object.defineProperty(createjs.Container.prototype, "tabEnabled", {
    value: true,
    enumerable: true,
    configurable: true,
    writable: true,
});

Object.defineProperty(createjs.Container.prototype, "tabIndex", {
    value: -1,
    enumerable: true,
    configurable: true,
    writable: true,
});

Object.defineProperty(createjs.Container.prototype, "needsSoftKeyboard", {
    value: false,
    enumerable: true,
    configurable: true,
    writable: true,
});

type ContainerWithListener = { _listeners: any, _dispatchEvent: Function } & createjs.Container;
const originalDispatchEvent = createjs.Container.prototype.dispatchEvent;
createjs.Container.prototype.dispatchEvent = function (this: ContainerWithListener, eventObj: Object | string | Event, target?: Object): boolean | void {
    // order if comparison is significat / unimportant (every Event is also an Object)
    if (eventObj instanceof Event || eventObj instanceof Object) {
        if ((eventObj as any)["type"] === "dblclick" && this.doubleClickEnabled === false) {
            return;
        }
    } else if (eventObj === "dblclick" && this.doubleClickEnabled === false) {
        return;
    }
    // copy the original dispatchEvent here, since we need to keep the event.target
    if (typeof eventObj === "string") {
        // skip everything if there's no listeners and it doesn't bubble:
        let listeners = this._listeners;
        if (!listeners || !listeners[eventObj]) {
            return true;
        }
        eventObj = new createjs.Event(eventObj);
    } else if ((eventObj as any).target && (eventObj as any).clone) {
        // redispatching an active event object, so clone it:
        // can't use createjs's clone, since we have augumanted attributes
        eventObj = (eventObj as any).clone();
    }

    // TODO: it would be nice to eliminate this. Maybe in favour of evtObj instanceof Event? Or !!evtObj.createEvent
    // try/catch allows redispatching of native events
    try {
        (eventObj as any).target = target || (eventObj as any).target || this;
    } catch (e) { }

    if (!(eventObj as any).bubbles || (eventObj as any).cancelBubble || !this.parent) {
        this._dispatchEvent(eventObj, 2);
    } else {
        let top = this,
            list = [top];
        while (top.parent) {
            list.push((top = top.parent as ContainerWithListener));
        }
        let i,
            l = list.length;

        // capture & atTarget
        for (i = l - 1; i >= 0 && !(eventObj as any).propagationStopped; i--) {
            list[i]._dispatchEvent(eventObj, 1 + (i === 0 ? 1 : 0));
        }
        // bubbling
        for (i = 1; i < l && !(eventObj as any).propagationStopped; i++) {
            list[i]._dispatchEvent(eventObj, 3);
        }
    }
    return !(eventObj as any).defaultPrevented;
};

createjs.Container.prototype.areInaccessibleObjectsUnderPoint = function (point: createjs.Point): boolean {
    logger.debug("Container.areInaccessibleObjectsUnderPoint not implemented yet");
    return false;
};

createjs.Container.prototype.stopAllMovieClips = function (): void { };
createjs.Container.prototype.requestSoftKeyboard = function (): boolean {
    return false;
};
createjs.Container.prototype.startTouchDrag = function (touchPointID: number, lockCenter: boolean = false, bounds: Rectangle | undefined = undefined): void { };
createjs.Container.prototype.stopTouchDrag = function (touchPointID: number): void { };
createjs.Container.prototype.removeChildren = function (this: ContainerWithNewShape) {
    this.removeAllChildren();
};

Object.defineProperty(createjs.Container.prototype, "dropTarget", {
    get: function (): DisplayObject | null {
        return null;
    },
    set: function (value: DisplayObject) {
        throw new Error("dropTarget has only getter");
    },
    enumerable: true,
    configurable: true,
});

createjs.Container.prototype.getBounds = function (targetSpace?: createjs.DisplayObject, ignoreInvisible: boolean = false): createjs.Rectangle {
    let bounds: createjs.Rectangle = (this as any)._getBounds(null, true, ignoreInvisible);

    if (!bounds) {
        bounds = new Rectangle(0, 0, 0, 0);
    }

    if (targetSpace && targetSpace != this) {
        return this.transformRect(bounds, targetSpace);
    } else {
        return bounds;
    }
};

(Container.prototype as any)._getBounds = function (matrix: createjs.Matrix2D, ignoreTransform: boolean, ignoreInvisible: boolean = false): Rectangle | null {
    let bounds = this.DisplayObject_getBounds();
    if (bounds) {
        return this._transformBounds(bounds, matrix, ignoreTransform);
    }

    let mtx = this._props.matrix;
    mtx = ignoreTransform ? mtx.identity() : this.getMatrix(mtx);
    if (matrix) {
        mtx.prependMatrix(matrix);
    }

    let l = this.children.length,
        rect: createjs.Rectangle | null = null;
    this.hitableChildCount = 0;
    const skipChildBounds = ignoreInvisible && this.mouseChildren;
    for (let i = 0; i < l; i++) {
        const child = this.children[i];
        if (skipChildBounds && !child.visible) {
            continue;
        }
        if (skipChildBounds && child.children && !child.children.length) {
            continue;
        }
        if (skipChildBounds && !child.mouseEnabled){
            continue;
        }
        if (!(bounds = child._getBounds(mtx, false, skipChildBounds))) {
            continue;
        } else {
            this.hitableChildCount++;
        }
        if (rect) {
            rect.extend(bounds.x, bounds.y, bounds.width, bounds.height);
        } else {
            rect = bounds.clone();
        }
    }
    return rect;
};

Object.defineProperty(createjs.Container.prototype, "useHandCursor", {
    get: function (this: createjs.Container) {
        return this.cursor == "pointer";
    },
    set: function (this: createjs.Container, value: boolean) {
        if (value) this.cursor = "pointer";
        else this.cursor = "default";
    },
    enumerable: true,
    configurable: true,
});

Object.defineProperty(createjs.Container.prototype, "loaderInfo", {
    get: function (): LoaderInfo {
        return new LoaderInfo();
    },
    enumerable: true,
    configurable: true,
});

Object.defineProperty(createjs.Container.prototype, "mouseX", {
    get: function (this: createjs.Container): number {
        if (this.stage) {
            let relativeBounds: createjs.Rectangle = this.getBounds(this.stage);
            let zoomFactor: number = 1;
            let pa: Container | null = this.parent;
            while (pa && !(pa instanceof Stage)) {
                zoomFactor *= pa.scaleX;
                pa = pa.parent;
            }

            return (this.stage.mouseX - relativeBounds.x) / zoomFactor;
        }
        return 0;
    },
    enumerable: true,
    configurable: true,
});
Object.defineProperty(createjs.Container.prototype, "mouseY", {
    get: function (this: createjs.Container): number {
        if (this.stage) {
            let relativeBounds: createjs.Rectangle = this.getBounds(this.stage);
            let zoomFactor: number = 1;
            let pa: Container | null = this.parent;
            while (pa && !(pa instanceof Stage)) {
                zoomFactor *= pa.scaleY;
                pa = pa.parent;
            }

            return (this.stage.mouseY - relativeBounds.y) / zoomFactor;
        }
        return 0;
    },
    enumerable: true,
    configurable: true,
});

Object.defineProperty(createjs.Container.prototype, "mousePos", {
    get: function (this: createjs.Container): createjs.Point {
        if (this.stage) return new createjs.Point(this.stage.mouseX - this.x, this.stage.mouseY - this.y);
        return new createjs.Point(0, 0);
    },
    enumerable: true,
    configurable: true,
});
Object.defineProperty(createjs.Container.prototype, "centerPoint", {
    get: function (this: createjs.Container): createjs.Point {
        return new createjs.Point(this.x + this.width / 2, this.y + this.height / 2);
    },
    enumerable: true,
    configurable: true,
});
createjs.Container.prototype.startDrag = function (lockCenter: boolean, dragBounds: createjs.Rectangle): void {
    const originPlace = { x: this.x, y: this.y };
    let prevDistance: { x: number; y: number } | null = null;
    this.on(PRESS_MOVE, function (evt: createjs.MouseEvent) {
        if (prevDistance == null) {
            prevDistance = { x: evt.stageX, y: evt.stageY };
        } else {
            const xDiff = prevDistance.x - evt.stageX;
            const yDiff = -(prevDistance.y - evt.stageY); // for our coordinate system, moving up means -y
            evt.currentTarget.x = Math.min(dragBounds.right, Math.max(originPlace.x - xDiff, dragBounds.left));
            evt.currentTarget.y = Math.min(dragBounds.bottom, Math.max(originPlace.y + yDiff, dragBounds.top));
        }
    });
};

type ContainerWithCenterPoint = { centerPoint: createjs.Point } & createjs.Container; // `centerPoint` is defined above

createjs.Container.prototype.startDragWithRotation = function (this: ContainerWithCenterPoint, lockCenter: boolean, dragBounds: createjs.Rectangle): void {
    let offset: createjs.Point = lockCenter ? this.centerPoint : this.mousePos;
    let p = this.parent; // ignore itself
    let angle = 0;
    while (p) {
        angle += p.rotation;
        p = p.parent;
    }
    let radians = angle / 180 * Math.PI;
    let sinR = Math.sin(radians);
    let cosR = Math.cos(radians);
    let originPlace = { x: this.x, y: this.y };
    this.on(PRESS_MOVE, function (evt: createjs.MouseEvent) {
        let xDiff = evt.stageX - offset.x;
        let yDiff = -(evt.stageY - offset.y); // for our coordinate system, moving up means -y
        let xRotated = ~~(xDiff * cosR - yDiff * sinR); // fast floor
        let yRotated = ~~(yDiff * cosR + xDiff + sinR);
        evt.target.x = Math.min(dragBounds.right, Math.max(originPlace.x + xRotated, dragBounds.left));
        evt.target.y = Math.min(dragBounds.bottom, Math.max(originPlace.y + yRotated, dragBounds.top));
    });
};

createjs.Container.prototype.stopDrag = function (): void {
    this.removeAllEventListeners(PRESS_MOVE);
};

createjs.Container.prototype.uncacheAllChildren = function (): void {
    const recursiveUncacheChildrenFunction = function (cont: createjs.Container) {
        if (cont) {
            for (let i: number = 0; i < cont.numChildren; i++) {
                let kid: createjs.DisplayObject = cont.getChildAt(i);

                // if it has filters and we uncache it, the filters would not be rendered anymore; stop here
                if (kid.filters && kid.filters.length > 0) {
                    return;
                }

                // uncache if it was cached
                if (kid.cacheCanvas) {
                    kid.uncache();
                }

                // bonus: make position clean to ensure sharpness of rendering
                kid.x = Math.floor(kid.x);
                kid.y = Math.floor(kid.y);

                // take next kids
                if (kid instanceof createjs.Container) {
                    recursiveUncacheChildrenFunction(kid as createjs.Container);
                }
            }
        }
    };
    recursiveUncacheChildrenFunction(this);
};

createjs.Container.prototype._getObjectsUnderPoint = function (this: createjs.Container, x: number, y: number, arr: any[] | null, mouse: boolean, activeListener?: any, currentDepth?: any): createjs.DisplayObject | null /*mgt todo stricter type?*/ {
    return objectsUnderPoint(this, createjs.DisplayObject._hitTestContext, x, y, arr, mouse, activeListener, currentDepth);
};

createjs.Container.prototype.__getObjectsUnderPoint = function (this: createjs.Container, x: number, y: number, arr: any[] | null, mouse: boolean, activeListener?: any, currentDepth?: any, hitTestTime?:number): createjs.DisplayObject | null /*mgt todo stricter type?*/ {
    return objectsUnderPoint(this, createjs.DisplayObject._hitTestContext, x, y, arr, mouse, activeListener, currentDepth, hitTestTime);
};

// copied from createJS, but we only want to tick, if it is visible and enabled
createjs.Container.prototype._tick = function (evtObj: any): void {
    if (this.tickEnabled && this.visible) {
        if (this.tickChildren) {
            for (let i = this.children.length - 1; i >= 0; i--) {
                let child = this.children[i];
                if (child.visible && child.tickEnabled && (child as any)._tick) {
                    (child as any)._tick(evtObj);
                }
            }
        }
        (this as any).DisplayObject__tick(evtObj);
    }
};

createjs.Container.prototype.changeTextFieldCacheScale = function(this: createjs.Container, scale: number): void {
    if (this.children && this.children.length > 0) {
        this.children.forEach(child => {
            if (child instanceof createjs.TextField) {
                child.defaultCacheScale = scale;
            } else if ( child instanceof createjs.Container) {
                child.changeTextFieldCacheScale(scale);
            }
        });
    }
};
createjs.Container.HIT_BOUNDS_CACHE_FREQUENCY = 333;
createjs.Container.prototype.getHitTestBounds = function(hitTestTime: number, targetSpace: DisplayObject): Rectangle {
    if ((hitTestTime - this.lastHitTestTime) > createjs.Container.HIT_BOUNDS_CACHE_FREQUENCY || !this.lastHitTestBounds) {
        this.lastHitTestTime = hitTestTime;
        if (this.parent && this.parent.hitableChildCount === 1 && this.parent.lastHitTestBounds) {
            if(this.children.length > 0 && this.mouseEnabled) {
                this.lastHitTestBounds = this.parent.lastHitTestBounds.clone();
            } else {
                return new Rectangle();
            }
        } else {
            this.lastHitTestBounds = this.getBounds(targetSpace, true);
        }
    }
    return this.lastHitTestBounds;
};
